import { ClipboardEvent, useCallback } from 'react';

const COPY_CONTAINER_ATTRIBUTE = 'data-copy-container';
const NO_COPY_ATTRIBUTE = 'data-no-copy';
const PLAINTEXT_PREFIX_ATTRIBUTE = 'data-plaintext-prefix';

interface CustomCopyBehaviorOptions {
  /** Ensures this element always wraps its children when they are copied  */
  isCopyContainer?: boolean;
  /** Prevents this element and its children from being copied */
  excludeFromCopy?: boolean;
  /** Adds this string as a prefix to this element's children when it is used as plaintext */
  plaintextPrefix?: string;
}

export const addCustomCopyBehavior = ({
  isCopyContainer,
  excludeFromCopy,
  plaintextPrefix,
}: CustomCopyBehaviorOptions) => {
  const dataAttributes: Record<string, string | boolean> = {};

  if (isCopyContainer) {
    dataAttributes[COPY_CONTAINER_ATTRIBUTE] = true;
  }
  if (excludeFromCopy) {
    dataAttributes[NO_COPY_ATTRIBUTE] = true;
  }
  if (plaintextPrefix) {
    dataAttributes[PLAINTEXT_PREFIX_ATTRIBUTE] = plaintextPrefix;
  }

  return dataAttributes;
};

// gets rid of additional newlines that come from block elements
// and handles indentation for list items
const extractTextContent = (element: Node, indentLevel: number = 0): string => {
  const children = Array.from(element.childNodes);
  const parts: string[] = [];
  let currentTextGroup = '';

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      // Accumulate consecutive text nodes together
      if (text.trim()) {
        currentTextGroup += text;
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      // Flush any accumulated text from consecutive text nodes
      if (currentTextGroup) {
        parts.push(currentTextGroup);
        currentTextGroup = '';
      }

      const elementChild = child as Element;
      if (elementChild.tagName === 'BR') {
        parts.push('');
      } else if (elementChild.tagName === 'LI') {
        parts.push(
          '   '.repeat(indentLevel) +
            extractTextContent(child, indentLevel + 1),
        );
      } else {
        parts.push(extractTextContent(child, indentLevel));
      }
    }
  }

  // Flush any remaining text
  if (currentTextGroup) {
    parts.push(currentTextGroup);
  }

  return parts.join('\n');
};

interface CopyHandlerOptions {
  skipTargetElements?: Array<'textarea' | 'input'>;
}

export const useCopyHandler = (options: CopyHandlerOptions = {}) => {
  const { skipTargetElements = [] } = options;

  const handleCopy = useCallback(
    (event: ClipboardEvent) => {
      for (const skipTargetElement of skipTargetElements) {
        switch (skipTargetElement) {
          case 'textarea':
            if (event.target instanceof HTMLTextAreaElement) return;
            break;
          case 'input':
            if (event.target instanceof HTMLInputElement) return;
            break;
        }
      }

      event.preventDefault();

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();
      const div = document.createElement('div');

      // determine what the outermost container for the copy selection should be
      const commonAncestor = range.commonAncestorContainer;
      const ancestorElement =
        commonAncestor.nodeType === Node.ELEMENT_NODE
          ? (commonAncestor as Element)
          : commonAncestor.parentElement;
      if (
        ancestorElement &&
        COPY_CONTAINER_ATTRIBUTE in ancestorElement.attributes
      ) {
        const listClone = document.createElement(
          ancestorElement.tagName.toLowerCase(),
        );
        for (const attr of Array.from(ancestorElement.attributes)) {
          listClone.setAttribute(attr.name, attr.value);
        }
        listClone.appendChild(fragment);
        div.appendChild(listClone);
      } else {
        div.appendChild(fragment);
      }

      // remove elements that shouldn't be copied
      const noCopyElements = div.querySelectorAll(`[${NO_COPY_ATTRIBUTE}]`);
      noCopyElements.forEach((el) => el.remove());

      event.clipboardData.setData('text/html', div.innerHTML);

      // add plaintext-only prefixes to copy data
      const hasPlaintextPrefix = div.querySelectorAll(
        `[${PLAINTEXT_PREFIX_ATTRIBUTE}]`,
      );
      hasPlaintextPrefix.forEach((el) => {
        const prefixContent = el.getAttribute(PLAINTEXT_PREFIX_ATTRIBUTE);
        if (prefixContent) {
          const textNode = document.createTextNode(prefixContent);
          el.insertBefore(textNode, el.firstChild);
        }
      });

      event.clipboardData.setData('text/plain', extractTextContent(div));
    },
    [skipTargetElements],
  );

  return handleCopy;
};
